import React, { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import {
  Table,
  Button,
  Input,
  InputNumber,
  Select,
  DatePicker,
  message,
  Modal,
  Tooltip,
  Spin,
  Form,
} from "antd";
import {
  PlusOutlined,
  DeleteOutlined,
  PrinterOutlined,
  ArrowLeftOutlined,
  UserAddOutlined,
} from "@ant-design/icons";
import dayjs from "dayjs";
import axiosInstance from "../utils/axiosInstance";
import { jsPDF } from "jspdf";

const { Option } = Select;

const emptyInvoice = () => ({
  id: null,
  invoiceNo: null,
  issueDate: dayjs(),
  dueDate: null,
  reference: "",
  customerId: null,
  customer: { name: "", address: "", vat: "" },
  lines: [],
  note: "",
});

function useBootSelection() {
  const location = useLocation();
  const stateSel = location.state?.selectedOrderIds || [];
  const pickup = sessionStorage.getItem("invoice:addFromProduction");
  if (pickup) {
    try {
      const parsed = JSON.parse(pickup);
      sessionStorage.removeItem("invoice:addFromProduction");
      return parsed;
    } catch {}
  }
  return stateSel;
}

const InvoiceManager = () => {
  const navigate = useNavigate();
  const params = useParams();
  const invoiceId = params.invoiceId ? Number(params.invoiceId) : null;
  const selectedAtStart = useBootSelection();

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [invoice, setInvoice] = useState(emptyInvoice());
  const [inventoryParts, setInventoryParts] = useState([]);

  // Contacts
  const [contacts, setContacts] = useState([]);
  const [contactModalOpen, setContactModalOpen] = useState(false);
  const [contactForm] = Form.useForm();

  // VAT selector
  const [headerVatPercent, setHeaderVatPercent] = useState(0);

  // Preview invoice number for NEW invoice
  const [previewInvoiceNo, setPreviewInvoiceNo] = useState("");

  // Part modal
  const [partModalOpen, setPartModalOpen] = useState(false);
  const [addingPart, setAddingPart] = useState({ productId: null, qty: 1, price: 0 });

  // Production Picker
  const [prodModalOpen, setProdModalOpen] = useState(false);
  const [prodLoading, setProdLoading] = useState(false);
  const [prodOrders, setProdOrders] = useState([]);
  const [prodSelectedRowKeys, setProdSelectedRowKeys] = useState([]);
  const [prodExistingIds, setProdExistingIds] = useState([]);
  const prodExistingSet = useMemo(() => new Set(prodExistingIds.map(Number)), [prodExistingIds]);

  // Lookup map: production id -> { sequence_no, sequence_month }
  const [prodLookup, setProdLookup] = useState({});

  // Seller/company info for PDF header
  const [seller, setSeller] = useState({
    name: "",
    addressLines: [],
    email: "",
    phone: "",
    vat: "",
  });

  const formatCurrency = (n) => {
    try {
      return new Intl.NumberFormat("en-GB", {
        style: "currency",
        currency: "GBP",
        maximumFractionDigits: 2,
      }).format(Number(n || 0));
    } catch {
      return `£${Number(n || 0).toFixed(2)}`;
    }
  };

  const normalizePreviewInvoice = (v) => {
    if (v == null || v === "") return "";
    const s = String(v).trim();
    if (!s) return "";
    if (/^inv[-\s]/i.test(s) || /^INV-/i.test(s)) return s;
    if (/^\d+$/.test(s)) return `INV-${s}`;
    return s;
  };

  const computeDisplaySequence = (record, allOrders) => {
    if (!record) return "-";
    if (!record.sequence_no || !record.sequence_month) {
      return String(record.id ?? "-");
    }

    const sameMonthOrders = (allOrders || [])
      .filter(
        (o) =>
          o &&
          o.sequence_month === record.sequence_month &&
          Number.isFinite(Number(o.sequence_no))
      )
      .slice();

    if (!sameMonthOrders.length) {
      return String(record.sequence_no);
    }

    sameMonthOrders.sort((a, b) => Number(b.sequence_no) - Number(a.sequence_no));
    const maxSeq = Number(sameMonthOrders[0].sequence_no) || Number(record.sequence_no) || 0;

    let indexInMonth = sameMonthOrders.findIndex((o) => o.id === record.id);

    if (indexInMonth === -1 && Number.isFinite(Number(record.sequence_no))) {
      indexInMonth = sameMonthOrders.findIndex(
        (o) => Number(o.sequence_no) === Number(record.sequence_no)
      );
    }

    if (indexInMonth === -1) {
      return String(record.sequence_no);
    }

    const displayNo = maxSeq - indexInMonth;
    return String(displayNo);
  };

  const totals = useMemo(() => {
    let subtotal = 0,
      tax = 0;
    (invoice.lines || []).forEach((l) => {
      const lt = Number(l.qty || 0) * Number(l.unitPrice || 0);
      subtotal += lt;
      tax += (lt * Number(l.taxRate || 0)) / 100;
    });
    return {
      subtotal: Number(subtotal.toFixed(2)),
      tax: Number(tax.toFixed(2)),
      grand: Number((subtotal + tax).toFixed(2)),
    };
  }, [invoice.lines]);

  const applyHeaderVatToAllLines = (percent) => {
    setInvoice((p) => ({
      ...p,
      lines: (p.lines || []).map((ln) => ({ ...ln, taxRate: Number(percent || 0) })),
    }));
  };

  const loadContacts = async () => {
    try {
      const { data } = await axiosInstance.get("/admin/invoices/contacts");
      const list = Array.isArray(data?.contacts) ? data.contacts : Array.isArray(data) ? data : [];
      setContacts(
        list.map((c) => ({
          id: c.id,
          name: c.name || "",
          address: c.address || "",
          vat: c.vat || "",
        }))
      );
    } catch {
      setContacts([]);
    }
  };

  const loadNextNumberPreview = async () => {
    try {
      const { data } = await axiosInstance.get("/admin/invoices/next-number");
      setPreviewInvoiceNo(normalizePreviewInvoice(data?.nextInvoiceNo || data?.next || ""));
    } catch {
      setPreviewInvoiceNo("");
    }
  };

  const loadSellerInfo = async () => {
    try {
      const { data } = await axiosInstance.get("/admin/invoices/seller");
      const s = data?.seller || data || {};
      const address = (s.address || s.addressText || "")
        .toString()
        .replace(/\r/g, "")
        .split("\n")
        .filter(Boolean);
      setSeller({
        name: s.name || s.companyName || "",
        addressLines: address,
        email: s.email || "",
        phone: s.phone || "",
        vat: s.vat || s.vatNumber || "",
      });
    } catch {
      const lsName = localStorage.getItem("companyName") || "";
      const lsAddr = (localStorage.getItem("companyAddress") || "").split("\n").filter(Boolean);
      const lsVat = localStorage.getItem("companyVat") || "";
      const lsEmail = localStorage.getItem("companyEmail") || "";
      const lsPhone = localStorage.getItem("companyPhone") || "";
      setSeller({
        name: lsName,
        addressLines: lsAddr,
        email: lsEmail,
        phone: lsPhone,
        vat: lsVat,
      });
    }
  };

  const loadProductionLookup = async () => {
    try {
      const token = localStorage.getItem("token");
      const { data } = await axiosInstance.get("/admin/production/list", {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      const list = Array.isArray(data?.orders) ? data.orders : Array.isArray(data) ? data : [];
      const map = {};
      list.forEach((o) => {
        const id = o.id;
        map[String(id)] = {
          sequence_no: o.sequence_no ?? o.sequenceNo ?? null,
          sequence_month: o.sequence_month ?? o.sequenceMonth ?? null,
        };
      });
      setProdLookup(map);
    } catch {
      setProdLookup({});
    }
  };

  useEffect(() => {
    const run = async () => {
      setLoading(true);
      try {
        await Promise.all([loadContacts(), loadSellerInfo(), loadProductionLookup()]);

        if (!invoiceId) await loadNextNumberPreview();

        let parts = [];
        try {
          const { data: partsRes } = await axiosInstance.get("/admin/invoices/inventory-parts");
          parts = Array.isArray(partsRes?.parts) ? partsRes.parts : [];
        } catch {
          try {
            const { data: partsRes2 } = await axiosInstance.get("/admin/inventory-parts");
            parts = Array.isArray(partsRes2?.parts) ? partsRes2.parts : [];
          } catch {}
        }
        setInventoryParts(parts);

        if (invoiceId) {
          const { data } = await axiosInstance.get(`/admin/invoices/${invoiceId}`);
          const inv = data?.invoice || data || {};
          const customer = inv.customer || {
            name: inv.customerName || "",
            address: inv.billTo || "",
            vat: inv.customerVat || "",
          };
          const contactId = inv.contactId || inv.customerId || null;

          let derivedVat = 0;
          if (Array.isArray(inv.items) && inv.items.length) {
            const first = Number(inv.items[0]?.taxRate || 0);
            const allSame = inv.items.every((it) => Number(it.taxRate || 0) === first);
            derivedVat = allSame ? first : 0;
          }

          setInvoice({
            id: inv.id ?? null,
            invoiceNo: inv.invoiceNo ?? null,
            issueDate: inv.issueDate ? dayjs(inv.issueDate) : dayjs(),
            dueDate: inv.dueDate ? dayjs(inv.dueDate) : null,
            reference: inv.reference || "",
            customerId: contactId,
            customer,
            lines:
              inv.lines ||
              inv.items?.map((it) => ({
                type: it.lineType || "production",
                refId: it.refId || null,
                description: it.description || "",
                qty: Number(it.quantity || it.qty || 1),
                unitPrice: Number(it.unitPrice || it.price || 0),
                taxRate: Number(it.taxRate || 0),
                sequence_no: it.sequence_no ?? it.sequenceNo ?? null,
                sequence_month: it.sequence_month ?? it.sequenceMonth ?? null,
              })) ||
              [],
            note: inv.note || "",
          });
          setHeaderVatPercent(derivedVat);
        } else {
          let prevLines = [];
          try {
            const rawPrev = sessionStorage.getItem("invoice:existingLines");
            if (rawPrev) {
              prevLines = JSON.parse(rawPrev);
              sessionStorage.removeItem("invoice:existingLines");
            }
          } catch {}
          let incomingLines = [];
          let suggestedRef = "";
          if (selectedAtStart?.length) {
            try {
              const { data } = await axiosInstance.get("/admin/invoices/production-lines", {
                params: { ids: selectedAtStart.join(",") },
              });
              if (Array.isArray(data?.lines)) incomingLines = data.lines;
              suggestedRef = (data?.reference || "").trim() || `ORD-${selectedAtStart[0]}`;
            } catch {}
            if (!incomingLines.length) {
              try {
                const raw = sessionStorage.getItem("invoice:selectedLinesPayload");
                if (raw) incomingLines = JSON.parse(raw) || [];
              } catch {}
            }
            try {
              sessionStorage.removeItem("invoice:selectedLinesPayload");
            } catch {}
          }
          const seen = new Set();
          const merged = [...prevLines, ...incomingLines]
            .filter((ln) => {
              const key = `${ln?.type || ""}:${ln?.refId || ""}`;
              if (seen.has(key)) return false;
              seen.add(key);
              return true;
            })
            .map((ln) => ({ ...ln, taxRate: Number(headerVatPercent || 0) }));
          setInvoice((p) => ({
            ...p,
            lines: merged.length ? merged : p.lines,
            reference: p.reference?.trim()
              ? p.reference
              : suggestedRef || p.reference || "",
          }));
        }
      } catch (e) {
        message.error(e?.response?.data?.message || "Failed to load invoice data");
      } finally {
        setLoading(false);
      }
    };
    run();
  }, [invoiceId]); // eslint-disable-line react-hooks/exhaustive-deps

  const onSelectContact = (id) => {
    const c = contacts.find((x) => Number(x.id) === Number(id));
    setInvoice((p) => ({
      ...p,
      customerId: id,
      customer: c
        ? { name: c.name || "", address: c.address || "", vat: c.vat || "" }
        : { name: "", address: "", vat: "" },
    }));
  };

  const openAddContact = () => {
    contactForm.resetFields();
    setContactModalOpen(true);
  };

  const saveContact = async () => {
    try {
      const vals = await contactForm.validateFields();
      const { data } = await axiosInstance.post("/admin/invoices/contacts", {
        name: vals.name?.trim(),
        address: vals.address?.trim() || "",
        vat: vals.vat?.trim() || "",
      });
      const created = data?.contact || data;
      await loadContacts();
      setInvoice((p) => ({
        ...p,
        customerId: created.id,
        customer: {
          name: created.name || "",
          address: created.address || "",
          vat: created.vat || "",
        },
      }));
      setContactModalOpen(false);
      message.success("Contact saved");
    } catch (e) {
      message.error(e?.response?.data?.message || "Failed to save contact");
    }
  };

  const addManualRow = () => {
    setInvoice((p) => ({
      ...p,
      lines: [
        ...(p.lines || []),
        {
          type: "manual",
          refId: null,
          description: "",
          qty: 1,
          unitPrice: 0,
          taxRate: Number(headerVatPercent || 0),
        },
      ],
    }));
  };

  const setLineFromInventory = (idx, partId) => {
    const part = inventoryParts.find((x) => Number(x.id) === Number(partId));
    if (!part) return;
    changeLine(idx, {
      type: "part",
      refId: part.id,
      description: part.product_name,
      unitPrice: Number(part.factory_price ?? part.price_euro ?? 0),
      taxRate: Number(headerVatPercent || 0),
    });
  };

  const columns = [
    {
      title: "Item",
      dataIndex: "description",
      key: "desc",
      render: (v, r, idx) => {
        const selectionValue =
          r.type === "part" && r.refId != null ? `inv_${r.refId}` : "__manual__";
        return (
          <div className="flex gap-2">
            <Select
              value={selectionValue}
              showSearch
              optionFilterProp="children"
              style={{ minWidth: 240 }}
              onChange={(val) => {
                if (val === "__manual__") {
                  changeLine(idx, { type: "manual", refId: null });
                } else if (String(val).startsWith("inv_")) {
                  const id = Number(String(val).split("_")[1]);
                  setLineFromInventory(idx, id);
                }
              }}
            >
              <Option value="__manual__">Manual entry</Option>
              {inventoryParts.map((p) => (
                <Option key={p.id} value={`inv_${p.id}`}>
                  {p.product_name}
                </Option>
              ))}
            </Select>
            <Input
              placeholder="Description"
              value={r.description}
              onChange={(e) => changeLine(idx, { description: e.target.value })}
            />
          </div>
        );
      },
    },
    {
      title: "Qty",
      dataIndex: "qty",
      key: "qty",
      width: 90,
      render: (v, r, idx) => (
        <InputNumber
          min={1}
          value={v}
          onChange={(n) => changeLine(idx, { qty: n })}
          style={{ width: "100%" }}
        />
      ),
    },
    {
      title: "Price",
      dataIndex: "unitPrice",
      key: "price",
      width: 140,
      render: (v, r, idx) => (
        <InputNumber
          min={0}
          value={v}
          onChange={(n) => changeLine(idx, { unitPrice: n })}
          style={{ width: "100%" }}
        />
      ),
    },
    {
      title: "Tax rate",
      dataIndex: "taxRate",
      key: "taxRate",
      width: 120,
      render: (v, r, idx) => (
        <InputNumber
          min={0}
          max={100}
          value={Number(v || 0)}
          formatter={(val) => `${val}%`}
          parser={(val) => Number(String(val).replace("%", ""))}
          onChange={(n) => changeLine(idx, { taxRate: Number(n || 0) })}
          style={{ width: "100%" }}
        />
      ),
    },
    {
      title: "Tax amount",
      key: "taxAmt",
      width: 140,
      render: (_, r) => {
        const lt = Number(r.qty || 0) * Number(r.unitPrice || 0);
        const tax = (lt * Number(r.taxRate || 0)) / 100;
        return <b>{formatCurrency(tax)}</b>;
      },
    },
    {
      title: "Amount",
      key: "total",
      width: 140,
      render: (_, r) => {
        const lt = Number(r.qty || 0) * Number(r.unitPrice || 0);
        const tax = (lt * Number(r.taxRate || 0)) / 100;
        return <b>{formatCurrency(lt + tax)}</b>;
      },
    },
    {
      title: "",
      key: "a",
      width: 64,
      render: (_, __, idx) => (
        <Button danger icon={<DeleteOutlined />} onClick={() => removeLine(idx)} />
      ),
    },
  ];

  const changeLine = (idx, patch) => {
    setInvoice((p) => {
      const lines = [...p.lines];
      lines[idx] = { ...lines[idx], ...patch };
      return { ...p, lines };
    });
  };

  const removeLine = (idx) =>
    setInvoice((p) => ({ ...p, lines: p.lines.filter((_, i) => i !== idx) }));

  const addInventoryPart = () => {
    const part = inventoryParts.find((x) => x.id === addingPart.productId);
    if (!part) return message.warning("Select a part first");
    setInvoice((p) => ({
      ...p,
      lines: [
        ...p.lines,
        {
          type: "part",
          refId: part.id,
          description: part.product_name,
          qty: Number(addingPart.qty || 1),
          unitPrice: Number(addingPart.price || part.factory_price || part.price_euro || 0),
          taxRate: Number(headerVatPercent || 0),
        },
      ],
    }));
    setAddingPart({ productId: null, qty: 1, price: 0 });
    setPartModalOpen(false);
  };

  const openProductionPicker = async () => {
    setProdModalOpen(true);
    setProdSelectedRowKeys([]);
    setProdLoading(true);
    try {
      const token = localStorage.getItem("token");
      const { data } = await axiosInstance.get("/admin/production/list", {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      const list = Array.isArray(data?.orders) ? data.orders : Array.isArray(data) ? data : [];

      const orderIds = list
        .map((o) => o.orderId || o.order_id || null)
        .filter(Boolean)
        .map((v) => String(v));

      let priceMap = {};
      if (orderIds.length) {
        try {
          const { data: priceData } = await axiosInstance.get("/admin/invoices/production-lines", {
            params: { ids: orderIds.join(",") },
          });
          const lines = Array.isArray(priceData?.lines) ? priceData.lines : [];
          lines.forEach((ln) => {
            const possibleOrderId =
              ln.orderId ?? ln.order_id ?? ln.order ? String(ln.order) : undefined;
            const key =
              possibleOrderId !== undefined
                ? String(possibleOrderId)
                : String(ln.refId ?? ln.id ?? "");

            priceMap[String(key)] = {
              unitPrice: Number(ln.unitPrice ?? ln.unit_price ?? ln.price ?? 0),
              qty: Number(ln.qty ?? ln.quantity ?? 1),
            };
          });
        } catch {
          priceMap = {};
        }
      }

      const normalized = list.map((o) => {
        const orderKey = String(o.orderId || o.order_id || o.order || "");
        const factoryPriceFromMap = orderKey ? priceMap[String(orderKey)]?.unitPrice ?? null : null;
        const qty = Number(o.quantity || o.qty || 1);

        const fallbackPrice =
          o.factory_price !== undefined && o.factory_price !== null
            ? Number(o.factory_price)
            : o.factoryPrice !== undefined && o.factoryPrice !== null
            ? Number(o.factoryPrice)
            : null;

        const priceVal =
          factoryPriceFromMap !== null && factoryPriceFromMap !== undefined
            ? Number(factoryPriceFromMap)
            : fallbackPrice !== null
            ? Number(fallbackPrice)
            : null;

        return {
          id: o.id,
          orderId: o.orderId || o.order_id || null,
          size: typeof o.size === "string" ? o.size : (o.size && o.size.size) || "N/A",
          product_name: o.product_name || o.productName || o.product || "",
          color: o.color || o.fabric_color_name || "",
          storageType: o.storageType || o.storage_type || "",
          quantity: qty,
          price: priceVal,
          lineTotal: priceVal !== null ? Number((priceVal * qty).toFixed(2)) : null,
          sequence_no: o.sequence_no ?? o.sequenceNo ?? null,
          sequence_month: o.sequence_month ?? o.sequenceMonth ?? null,
          factory_price: o.factory_price ?? o.factoryPrice ?? null,
        };
      });

      setProdOrders(normalized);

      const map = {};
      normalized.forEach((r) => {
        map[String(r.id)] = {
          sequence_no: r.sequence_no ?? null,
          sequence_month: r.sequence_month ?? null,
        };
      });
      setProdLookup((prev) => ({ ...prev, ...map }));

      const existingIdsArr = (invoice.lines || [])
        .filter((ln) => ln.type === "production" && ln.refId != null)
        .map((ln) => Number(ln.refId));
      setProdExistingIds(existingIdsArr);

      const existingSet = new Set(existingIdsArr);
      setProdSelectedRowKeys(normalized.filter((r) => existingSet.has(Number(r.id))).map((r) => r.id));
    } catch (e) {
      message.error(e?.response?.data?.message || "Failed to load production orders");
      setProdOrders([]);
      setProdExistingIds([]);
      setProdSelectedRowKeys([]);
    } finally {
      setProdLoading(false);
    }
  };

  const addSelectedProductionToInvoice = async () => {
    const newProdIds = prodSelectedRowKeys.filter((id) => !prodExistingSet.has(Number(id)));

    if (!newProdIds.length) {
      setProdModalOpen(false);
      return;
    }

    const orderIds = newProdIds
      .map((prodId) => {
        const r = prodOrders.find((p) => Number(p.id) === Number(prodId));
        return r?.orderId ?? null;
      })
      .filter(Boolean);

    let idsToSend;
    if (orderIds.length) {
      idsToSend = orderIds.map((x) => String(x));
    } else {
      idsToSend = newProdIds.map((x) => String(x));
    }

    try {
      const { data } = await axiosInstance.get("/admin/invoices/production-lines", {
        params: { ids: idsToSend.join(",") },
      });

      const newLines = (data.lines || []).map((ln) => ({
        ...ln,
        taxRate: Number(headerVatPercent || 0),
      }));

      setInvoice((p) => {
        const existing = new Set((p.lines || []).map((x) => `${x.type}:${x.refId}`));
        const toAdd = newLines.filter((ln) => {
          const key = `${ln.type || "production"}:${ln.refId ?? ""}`;
          if (existing.has(key)) return false;
          existing.add(key);
          return true;
        });
        return { ...p, lines: [...p.lines, ...toAdd] };
      });

      setProdModalOpen(false);
    } catch {
      message.error("Failed to load production prices");
    }
  };

  const buildPayload = () => ({
    contactId: invoice.customerId || null,
    customerName: invoice.customer?.name || "",
    billTo: [invoice.customer?.name, invoice.customer?.address].filter(Boolean).join("\n"),
    shipTo: "",
    issueDate: invoice.issueDate?.format("YYYY-MM-DD") || null,
    dueDate: invoice.dueDate?.format("YYYY-MM-DD") || null,
    reference: invoice.reference,
    notes: invoice.note,
    items: (invoice.lines || []).map((l) => ({
      lineType:
        l.type === "part"
          ? "inventory"
          : l.type === "manual"
          ? "manual"
          : "production",
      refId: l.refId || null,
      description: l.description,
      quantity: Number(l.qty || 1),
      unitPrice: Number(l.unitPrice || 0),
      taxRate: Number(l.taxRate || 0),
      sequence_no: l.sequence_no ?? null,
      sequence_month: l.sequence_month ?? null,
    })),
  });

  const saveDraft = async () => {
    setSaving(true);
    try {
      if (!invoice.id) {
        const { data } = await axiosInstance.post("/admin/invoices", buildPayload());
        const inv = data?.invoice || data || {};
        setInvoice((p) => ({
          ...p,
          id: inv.id ?? p.id,
          invoiceNo: inv.invoiceNo || p.invoiceNo || null,
        }));
      } else {
        const { data } = await axiosInstance.put(`/admin/invoices/${invoice.id}`, buildPayload());
        const inv = data?.invoice || data || {};
        setInvoice((p) => ({
          ...p,
          id: inv.id ?? p.id,
          invoiceNo: inv.invoiceNo || p.invoiceNo || null,
        }));
      }
      message.success("Draft saved");
    } catch (e) {
      message.error(e?.response?.data?.message || "Failed to save draft");
    } finally {
      setSaving(false);
    }
  };

  const finalize = async () => {
    setSaving(true);
    try {
      let inv;
      if (!invoice.id) {
        const { data } = await axiosInstance.post("/admin/invoices", buildPayload());
        inv = data?.invoice || data || {};
      } else {
        const { data } = await axiosInstance.put(`/admin/invoices/${invoice.id}`, buildPayload());
        inv = data?.invoice || data || {};
      }
      setInvoice((p) => ({
        ...p,
        id: inv.id ?? p.id,
        invoiceNo: inv.invoiceNo || p.invoiceNo || null,
      }));
      message.success(`Invoice generated: ${inv.invoiceNo || "INV-###"}`);
    } catch (e) {
      message.error(e?.response?.data?.message || "Failed to generate invoice");
    } finally {
      setSaving(false);
    }
  };

  const printPdf = () => {
    const doc = new jsPDF({ unit: "pt", format: "a4" });
    doc.setProperties({ title: `Invoice ${invoice.invoiceNo || ""}`, subject: "Invoice" });

    const pageW = doc.internal.pageSize.getWidth();
    const pageH = doc.internal.pageSize.getHeight();
    const M = 60;
    const G = 18;
    const contentW = pageW - M * 2;

    doc.setFont("helvetica", "bold");
    doc.setFontSize(28);
    doc.text("INVOICE", M, 80);

    const leftW = Math.round(contentW * 0.42);
    const metaW = Math.round(contentW * 0.25);
    const rightW = contentW - leftW - metaW - G * 2;

    const leftX = M;
    const metaX = leftX + leftW + G;
    const rightX = metaX + metaW + G;

    const headerY = 112;

    const boxH = 150;
    doc.setLineWidth(1);
    doc.rect(leftX, headerY, leftW, boxH);
    doc.setFontSize(11);
    doc.setFont("helvetica", "bold");
    doc.text("Deliver to", leftX + 12, headerY + 18);
    doc.setFont("helvetica", "normal");

    const deliverLinesRaw = [
      invoice.customer?.name || "",
      invoice.customer?.address || "",
      invoice.customer?.vat ? `VAT: ${invoice.customer.vat}` : "",
    ]
      .filter(Boolean)
      .join("\n");
    const deliverLines = doc.splitTextToSize(deliverLinesRaw, leftW - 24);
    let dy = headerY + 40;
    deliverLines.forEach((l) => {
      doc.text(l, leftX + 12, dy);
      dy += 16;
    });

    let y = headerY;
    doc.setFont("helvetica", "bold");
    doc.text("Invoice Date", rightX, y + 10);
    doc.setFont("helvetica", "normal");
    doc.text(invoice.issueDate ? invoice.issueDate.format("DD MMM YYYY") : "-", rightX, y + 28);

    doc.setFont("helvetica", "bold");
    doc.text("Due Date", rightX, y + 52);
    doc.setFont("helvetica", "normal");
    doc.text(invoice.dueDate ? invoice.dueDate.format("DD MMM YYYY") : "-", rightX, y + 70);

    doc.setFont("helvetica", "bold");
    doc.text("Invoice Number", rightX, y + 94);
    doc.setFont("helvetica", "normal");
    const displayInv = invoice.invoiceNo || previewInvoiceNo || "INV-###";
    doc.text(displayInv, rightX, y + 112);

    if (invoice.reference?.trim()) {
      doc.setFont("helvetica", "bold");
      doc.text("Reference", rightX, y + 136);
      doc.setFont("helvetica", "normal");
      const refLines = doc.splitTextToSize(invoice.reference, rightW);
      doc.text(refLines, rightX, y + 154);
    }

    y = headerY;
    doc.setFont("helvetica", "bold");
    const sellerName = doc.splitTextToSize(seller.name || "", metaW);
    if (sellerName.length) doc.text(sellerName, metaX, y + 10);
    doc.setFont("helvetica", "normal");
    let sy = y + (sellerName.length ? 28 : 10);
    const sellerAddr = doc.splitTextToSize((seller.addressLines || []).join("\n"), metaW);
    sellerAddr.forEach((l) => {
      doc.text(l, metaX, sy);
      sy += 16;
    });
    if (seller.email) {
      doc.text(`Email: ${seller.email}`, metaX, sy);
      sy += 16;
    }
    if (seller.phone) {
      doc.text(`Phone: ${seller.phone}`, metaX, sy);
      sy += 16;
    }
    if (seller.vat) {
      doc.setFont("helvetica", "bold");
      doc.text("VAT Number", metaX, sy + 8);
      doc.setFont("helvetica", "normal");
      doc.text(String(seller.vat), metaX, sy + 24);
    }

    const tableTop = headerY + boxH + 30;

    const itemW = 60;
    const qtyW = 60;
    const priceW = 90;
    const totalW = 100;

    const totalRight = Math.round(M + contentW);
    const totalX = totalRight - totalW;
    const priceRight = totalX - G;
    const priceX = priceRight - priceW;
    const qtyRight = priceX - G;
    const qtyX = qtyRight - qtyW;
    const itemX = M;
    const descX = itemX + itemW + G;
    const descW = Math.max(120, Math.floor(qtyX - descX - G));

    const drawHeader = () => {
      doc.setLineWidth(1);
      doc.line(M, tableTop, M + contentW, tableTop);
      doc.setFont("helvetica", "bold");
      doc.text("Item", itemX, tableTop + 18);
      doc.text("Description", descX, tableTop + 18);
      doc.text("Qty", qtyRight, tableTop + 18, { align: "right" });
      doc.text("Unit Price", priceRight, tableTop + 18, { align: "right" });
      doc.text("Line Total", totalRight, tableTop + 18, { align: "right" });
      doc.line(M, tableTop + 28, M + contentW, tableTop + 28);
    };

    drawHeader();

    let rowY = tableTop + 48;
    const lineH = 16;
    const ROW_PAD_TOP = 5;
    const ROW_PAD_BOTTOM = 6;

    doc.setFont("helvetica", "normal");
    (invoice.lines || []).forEach((ln) => {
      const code = (() => {
        if (ln.sku) return ln.sku;
        if (ln.type === "production") {
          if (ln.sequence_no) return String(ln.sequence_no);
          const lookup = prodLookup[String(ln.refId)];
          if (lookup && (lookup.sequence_no ?? null) != null) return String(lookup.sequence_no);
          return String(ln.refId || "");
        }
        return String(ln.refId ?? "");
      })();

      const qty = Number(ln.qty || 0);
      const price = Number(ln.unitPrice || 0);
      const lineTotal = qty * price;

      const descLines = doc.splitTextToSize(String(ln.description || ""), descW);
      const neededH = Math.max(lineH, descLines.length * lineH) + ROW_PAD_TOP + ROW_PAD_BOTTOM;

      if (rowY + neededH > pageH - M - 140) {
        doc.addPage();
        drawHeader();
        rowY = tableTop + 48;
      }

      const textY = rowY + ROW_PAD_TOP;
      doc.text(String(code || ""), itemX, textY);
      doc.text(descLines, descX, textY);
      doc.text(qty.toFixed(2), qtyRight, textY, { align: "right" });
      doc.text(formatCurrency(price), priceRight, textY, { align: "right" });
      doc.text(formatCurrency(lineTotal), totalRight, textY, { align: "right" });

      const ruleY = rowY + neededH - ROW_PAD_BOTTOM;
      doc.setDrawColor(230, 230, 230);
      doc.line(M, ruleY, M + contentW, ruleY);
      doc.setDrawColor(0, 0, 0);

      rowY += neededH;
    });

    const totalsTop = Math.min(rowY + 18, pageH - M - 104);
    const boxW = 260;
    const boxX = M + contentW - boxW;
    const y0 = totalsTop;

    doc.setLineWidth(1);
    doc.rect(boxX, y0, boxW, 104);
    doc.setFont("helvetica", "normal");
    doc.text("Subtotal", boxX + 12, y0 + 24);
    doc.text("Total VAT", boxX + 12, y0 + 44);
    doc.setFont("helvetica", "bold");
    doc.text("Total", boxX + 12, y0 + 74);

    doc.setFont("helvetica", "normal");
    doc.text(formatCurrency(totals.subtotal), boxX + boxW - 12, y0 + 24, { align: "right" });
    doc.text(formatCurrency(totals.tax), boxX + boxW - 12, y0 + 44, { align: "right" });
    doc.setFont("helvetica", "bold");
    doc.text(formatCurrency(totals.grand), boxX + boxW - 12, y0 + 74, { align: "right" });

    const vatPolicy = headerVatPercent
      ? `Amounts include VAT at ${headerVatPercent}% where applicable.`
      : "No VAT applied.";
    doc.setFont("helvetica", "normal");
    const noteLines = doc.splitTextToSize(vatPolicy, contentW - 280);
    doc.text(noteLines, M, y0 + 16);

    if (invoice.note?.trim()) {
      const ny = y0 + 104 + 16;
      const nl = doc.splitTextToSize(invoice.note.trim(), contentW);
      let nyPos = ny;
      nl.forEach((line) => {
        if (nyPos > pageH - M) {
          doc.addPage();
          nyPos = M;
        }
        doc.text(line, M, nyPos);
        nyPos += 16;
      });
    }

    doc.save((displayInv || "invoice_draft") + ".pdf");
  };

 const generateAndPrint = async () => {
  try {
    setSaving(true);
    printPdf();
  } catch (e) {
    message.error("Failed to download invoice");
  } finally {
    setSaving(false);
  }
};

  return (
    <Spin spinning={loading || saving} size="large" className="min-h-screen">
      <div className="min-h-screen bg-gradient-to-br from-gray-50 to-blue-50 py-6 px-4">
        <div className="max-w-7xl mx-auto">
          {/* Header */}
          <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-6 mb-6">
            <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
              <div className="flex items-start gap-3">
                <Button
                  icon={<ArrowLeftOutlined />}
                  onClick={() => navigate(-1)}
                  className="rounded-lg mt-1"
                />
                <div>
                  <h1 className="text-2xl font-bold text-gray-900 mb-2">
                    {invoice.invoiceNo ? `Invoice ${invoice.invoiceNo}` : "New Invoice"}
                  </h1>
                  <p className="text-gray-600">
                    Create and manage invoice details, items, VAT and customer billing information
                  </p>
                </div>
              </div>

              <div className="flex flex-wrap gap-2">
                <Button onClick={saveDraft} loading={saving} className="rounded-lg font-medium">
                  Save Draft
                </Button>
                <Button onClick={finalize} loading={saving} className="rounded-lg font-medium">
                  Generate Invoice
                </Button>
                <Button
                  icon={<PrinterOutlined />}
                  loading={saving}
                  onClick={generateAndPrint}
                  type="primary"
                  className="bg-blue-500 hover:bg-blue-600 border-blue-500 text-white rounded-lg font-medium"
                >
                  Print / Download
                </Button>
              </div>
            </div>
          </div>

          {/* Invoice Details */}
          <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-6 mb-6">
            <div className="mb-5">
              <h2 className="text-lg font-semibold text-gray-900 mb-1">Invoice Details</h2>
              <p className="text-sm text-gray-500">Basic invoice information and customer selection</p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
              <div className="flex flex-col">
                <label className="text-sm font-medium text-gray-700 mb-2">Contact</label>
                <div className="flex gap-2">
                  <Select
                    showSearch
                    placeholder="Select contact"
                    value={invoice.customerId ?? undefined}
                    onChange={onSelectContact}
                    optionFilterProp="children"
                    style={{ flex: 1 }}
                    allowClear
                    className="invoice-input"
                  >
                    {contacts.map((c) => (
                      <Option key={c.id} value={c.id}>
                        {c.name}
                      </Option>
                    ))}
                  </Select>
                  <Tooltip title="Add Customer">
                    <Button icon={<UserAddOutlined />} onClick={openAddContact} className="rounded-lg" />
                  </Tooltip>
                </div>
              </div>

              <div className="flex flex-col">
                <label className="text-sm font-medium text-gray-700 mb-2">Issue Date</label>
                <DatePicker
                  style={{ width: "100%" }}
                  value={invoice.issueDate}
                  onChange={(d) => setInvoice((p) => ({ ...p, issueDate: d }))}
                  className="invoice-input"
                />
              </div>

              <div className="flex flex-col">
                <label className="text-sm font-medium text-gray-700 mb-2">Due Date</label>
                <DatePicker
                  style={{ width: "100%" }}
                  value={invoice.dueDate}
                  onChange={(d) => setInvoice((p) => ({ ...p, dueDate: d }))}
                  className="invoice-input"
                />
              </div>

              <div className="flex flex-col md:col-span-2">
                <label className="text-sm font-medium text-gray-700 mb-2">Reference</label>
                <Input
                  value={invoice.reference}
                  onChange={(e) => setInvoice((p) => ({ ...p, reference: e.target.value }))}
                  className="invoice-input"
                  placeholder="Enter reference"
                />
              </div>

              <div className="flex flex-col">
                <label className="text-sm font-medium text-gray-700 mb-2">Invoice Number</label>
                <Input
                  value={invoice.invoiceNo || previewInvoiceNo || "—"}
                  onChange={(e) => setInvoice((p) => ({ ...p, invoiceNo: e.target.value }))}
                  className="invoice-input"
                />
              </div>

              <div className="flex flex-col">
                <label className="text-sm font-medium text-gray-700 mb-2">Bill To (Name)</label>
                <Input
                  value={invoice.customer?.name || ""}
                  onChange={(e) =>
                    setInvoice((p) => ({
                      ...p,
                      customer: { ...p.customer, name: e.target.value },
                    }))
                  }
                  className="invoice-input"
                  placeholder="Customer name"
                />
              </div>

              <div className="flex flex-col">
                <label className="text-sm font-medium text-gray-700 mb-2">Customer VAT</label>
                <Input
                  value={invoice.customer?.vat || ""}
                  onChange={(e) =>
                    setInvoice((p) => ({
                      ...p,
                      customer: { ...p.customer, vat: e.target.value },
                    }))
                  }
                  className="invoice-input"
                  placeholder="VAT number"
                />
              </div>

              <div className="flex flex-col md:col-span-2 xl:col-span-3">
                <label className="text-sm font-medium text-gray-700 mb-2">Bill To (Address)</label>
                <Input.TextArea
                  rows={3}
                  value={invoice.customer?.address || ""}
                  onChange={(e) =>
                    setInvoice((p) => ({
                      ...p,
                      customer: { ...p.customer, address: e.target.value },
                    }))
                  }
                  className="invoice-textarea"
                  placeholder="Customer address"
                />
              </div>

              <div className="flex flex-col md:col-span-2 xl:col-span-3">
                <label className="text-sm font-medium text-gray-700 mb-2">Amounts Are</label>
                <div className="flex flex-wrap items-center gap-3">
                  <Select
                    value={headerVatPercent === 0 ? "no_vat" : "vat_20"}
                    onChange={(val) => {
                      const pct = val === "no_vat" ? 0 : 20;
                      setHeaderVatPercent(pct);
                      applyHeaderVatToAllLines(pct);
                    }}
                    style={{ width: 220 }}
                    className="invoice-input"
                  >
                    <Option value="no_vat">No VAT</Option>
                    <Option value="vat_20">VAT 20%</Option>
                  </Select>
                  <InputNumber
                    min={0}
                    max={100}
                    value={headerVatPercent}
                    disabled={headerVatPercent === 0}
                    formatter={(v) => `${v}%`}
                    parser={(v) => Number(String(v).replace("%", ""))}
                    onChange={(n) => {
                      const pct = Number(n || 0);
                      setHeaderVatPercent(pct);
                      applyHeaderVatToAllLines(pct);
                    }}
                    className="invoice-input"
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Items */}
          <div className="bg-white rounded-2xl shadow-sm border border-gray-200 overflow-hidden mb-6">
            <div className="p-6 border-b border-gray-200">
              <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
                <div>
                  <h2 className="text-lg font-semibold text-gray-900 mb-1">Invoice Items</h2>
                  <p className="text-sm text-gray-500">Add manual rows, inventory items or production items</p>
                </div>

                <div className="flex flex-wrap gap-2">
                  <Button icon={<PlusOutlined />} onClick={addManualRow} className="rounded-lg font-medium">
                    Add Row
                  </Button>
                  <Button onClick={openProductionPicker} className="rounded-lg font-medium">
                    Add from Production
                  </Button>
                  <Button
                    type="primary"
                    icon={<PlusOutlined />}
                    onClick={() => setPartModalOpen(true)}
                    className="bg-blue-500 hover:bg-blue-600 border-blue-500 text-white rounded-lg font-medium"
                  >
                    Add From Inventory
                  </Button>
                </div>
              </div>
            </div>

            <Table
              dataSource={invoice.lines.map((x, i) => ({ key: i, ...x }))}
              columns={columns}
              pagination={false}
              size="middle"
              bordered={false}
              loading={loading}
              scroll={{ x: "max-content" }}
              className="modern-invoice-table"
              locale={{
                emptyText: (
                  <div className="text-center py-10">
                    <div className="text-lg font-medium text-gray-500 mb-2">No invoice items yet</div>
                    <div className="text-sm text-gray-400">
                      Start by adding a row, production item, or inventory item
                    </div>
                  </div>
                ),
              }}
            />
          </div>

          {/* Totals */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            <div className="lg:col-span-2 bg-white rounded-2xl shadow-sm border border-gray-200 p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-1">Notes</h2>
              <p className="text-sm text-gray-500 mb-4">Optional invoice notes for PDF or customer reference</p>
              <Input.TextArea
                rows={5}
                value={invoice.note}
                onChange={(e) => setInvoice((p) => ({ ...p, note: e.target.value }))}
                className="invoice-textarea"
                placeholder="Add invoice note..."
              />
            </div>

            <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">Invoice Summary</h2>

              <div className="space-y-3">
                <div className="flex justify-between items-center py-2 border-b border-gray-100">
                  <span className="text-gray-600">Subtotal</span>
                  <span className="font-medium text-gray-900">{formatCurrency(totals.subtotal)}</span>
                </div>

                <div className="flex justify-between items-center py-2 border-b border-gray-100">
                  <span className="text-gray-600">Total VAT</span>
                  <span className="font-medium text-gray-900">{formatCurrency(totals.tax)}</span>
                </div>

                <div className="flex justify-between items-center pt-3">
                  <span className="text-lg font-semibold text-gray-900">Total</span>
                  <span className="text-xl font-bold text-blue-600">{formatCurrency(totals.grand)}</span>
                </div>
              </div>
            </div>
          </div>

          {/* Inventory Modal */}
          <Modal
            title="Add From Inventory"
            open={partModalOpen}
            onCancel={() => {
              setPartModalOpen(false);
              setAddingPart({ productId: null, qty: 1, price: 0 });
            }}
            onOk={addInventoryPart}
            okText="Add"
            className="modern-modal"
          >
            <div className="space-y-3">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Part</label>
                <Select
                  value={addingPart.productId}
                  onChange={(v) => setAddingPart((p) => ({ ...p, productId: v }))}
                  style={{ width: "100%" }}
                  placeholder="Select part"
                  showSearch
                  optionFilterProp="children"
                  className="invoice-input"
                >
                  {inventoryParts.map((p) => (
                    <Option key={p.id} value={p.id}>
                      {p.product_name}
                    </Option>
                  ))}
                </Select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Qty</label>
                <InputNumber
                  min={1}
                  value={addingPart.qty}
                  onChange={(n) => setAddingPart((p) => ({ ...p, qty: n }))}
                  style={{ width: "100%" }}
                  className="invoice-input"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Unit Price</label>
                <InputNumber
                  min={0}
                  value={addingPart.price}
                  onChange={(n) => setAddingPart((p) => ({ ...p, price: n }))}
                  style={{ width: "100%" }}
                  className="invoice-input"
                />
              </div>
            </div>
          </Modal>

          {/* Production Modal */}
          <Modal
            title="Select Production Orders"
            open={prodModalOpen}
            onCancel={() => setProdModalOpen(false)}
            onOk={addSelectedProductionToInvoice}
            okText={`Add Selected (${prodSelectedRowKeys.filter((id) => !prodExistingSet.has(Number(id))).length})`}
            width={980}
            className="modern-modal"
          >
            <Spin spinning={prodLoading}>
              <Table
                rowKey="id"
                dataSource={prodOrders}
                pagination={{ pageSize: 10 }}
                rowSelection={{
                  selectedRowKeys: prodSelectedRowKeys,
                  onChange: (keys) => {
                    const union = new Set([...prodExistingIds, ...keys]);
                    setProdSelectedRowKeys([...union]);
                  },
                  getCheckboxProps: (record) => ({
                    disabled: prodExistingSet.has(Number(record.id)),
                  }),
                }}
                className="modern-invoice-table"
                columns={[
                  {
                    title: "No",
                    key: "seq_no",
                    width: 80,
                    render: (_, record) => {
                      try {
                        const display = computeDisplaySequence(record, prodOrders);
                        return <span className="font-mono">{display}</span>;
                      } catch {
                        return <span className="font-mono">{String(record.id)}</span>;
                      }
                    },
                  },
                  { title: "Size", dataIndex: "size", width: 120 },
                  { title: "Product", dataIndex: "product_name", width: 220 },
                  { title: "Color", dataIndex: "color", width: 140 },
                  { title: "Storage", dataIndex: "storageType", width: 140 },
                  {
                    title: "Qty",
                    dataIndex: "quantity",
                    width: 80,
                    render: (q) => <span className="font-semibold">{q}</span>,
                  },
                  {
                    title: "Unit Price (£)",
                    width: 140,
                    render: (_, r) =>
                      r.price !== null && r.price !== undefined ? formatCurrency(r.price) : "—",
                  },
                  {
                    title: "Line Total",
                    width: 140,
                    render: (_, r) =>
                      r.lineTotal !== null && r.lineTotal !== undefined ? formatCurrency(r.lineTotal) : "—",
                  },
                ]}
              />
            </Spin>
          </Modal>

          {/* Contact Modal */}
          <Modal
            title="Add Contact"
            open={contactModalOpen}
            onCancel={() => setContactModalOpen(false)}
            onOk={saveContact}
            okText="Save Contact"
            className="modern-modal"
          >
            <Form form={contactForm} layout="vertical">
              <Form.Item
                label="Name / Company"
                name="name"
                rules={[{ required: true, message: "Please enter a name" }]}
              >
                <Input className="invoice-input" />
              </Form.Item>

              <Form.Item label="Address" name="address">
                <Input.TextArea rows={3} className="invoice-textarea" />
              </Form.Item>

              <Form.Item
                label="Email"
                name="email"
                rules={[{ type: "email", message: "Please enter a valid email address" }]}
              >
                <Input placeholder="Enter email address" className="invoice-input" />
              </Form.Item>

              <Form.Item label="VAT Number" name="vat">
                <Input className="invoice-input" />
              </Form.Item>
            </Form>
          </Modal>
        </div>
      </div>

      <style>{`
        .invoice-input.ant-input,
        .invoice-input .ant-select-selector,
        .invoice-input.ant-picker,
        .invoice-input.ant-input-number,
        .invoice-textarea.ant-input {
          border-radius: 10px !important;
        }

        .modern-invoice-table .ant-table-thead > tr > th {
          background: #f8fafc !important;
          color: #374151 !important;
          font-weight: 600;
          border-bottom: 2px solid #e5e7eb !important;
          white-space: nowrap;
          padding: 12px 16px !important;
        }

        .modern-invoice-table .ant-table-tbody > tr > td {
          border-bottom: 1px solid #f1f5f9 !important;
          padding: 12px 16px !important;
          transition: all 0.2s ease;
          background: #fff;
        }

        .modern-invoice-table .ant-table-tbody > tr:hover > td {
          background: #f9fafb !important;
        }

        .modern-invoice-table .ant-table-container {
          border: none !important;
        }

        .modern-invoice-table .ant-table {
          border-radius: 0 !important;
        }

        .modern-modal .ant-modal-content {
          border-radius: 16px;
          overflow: hidden;
        }

        .modern-modal .ant-modal-header {
          border-bottom: 1px solid #e5e7eb;
          background: #f8fafc;
        }
      `}</style>
    </Spin>
  );
};

export default InvoiceManager;